// Overflow-aware prev/next state for the Product Hotspot Recs carousel.
// Kept as a near-copy of product-family.js (scoped to .product-hotspot-recs-slideshow)
// to avoid modifying the shared product-family behavior. Drives disabled state from
// real scroll position rather than the slideshow's visibleSlides count, which can be
// stale and produce false "both disabled" states.
//
// Loaded as an ES module, so top-level declarations are module-scoped (no IIFE needed).

/**
 * Set a button's disabled state only when it differs, so re-asserting our state
 * doesn't create redundant mutations (which would loop the disabled observer).
 * @param {HTMLButtonElement} btn
 * @param {boolean} value
 */
function setDisabled(btn, value) {
  if (btn.disabled !== value) btn.disabled = value;
}

/** @param {HTMLElement} slideshow */
function syncButtons(slideshow) {
  const scroller = /** @type {HTMLElement | null} */ (slideshow.querySelector('slideshow-slides'));
  const nextBtn = /** @type {HTMLButtonElement | null} */ (slideshow.querySelector('[ref="next"]'));
  const prevBtn = /** @type {HTMLButtonElement | null} */ (
    slideshow.querySelector('[ref="previous"]')
  );
  if (!scroller || !nextBtn || !prevBtn) return;

  setDisabled(prevBtn, scroller.scrollLeft < 1);
  setDisabled(nextBtn, scroller.scrollLeft + scroller.offsetWidth >= scroller.scrollWidth - 1);
  syncArrowVisibility(slideshow);
}

/** @param {HTMLElement} slideshow */
function syncArrowVisibility(slideshow) {
  const nextBtn = /** @type {HTMLButtonElement | null} */ (slideshow.querySelector('[ref="next"]'));
  const prevBtn = /** @type {HTMLButtonElement | null} */ (
    slideshow.querySelector('[ref="previous"]')
  );
  if (!nextBtn || !prevBtn) return;
  slideshow.classList.toggle('both-arrows-disabled', prevBtn.disabled && nextBtn.disabled);
}

/** @param {HTMLElement} slideshow */
function updateOverflow(slideshow) {
  const scroller = /** @type {HTMLElement | null} */ (slideshow.querySelector('slideshow-slides'));
  if (!scroller) return;
  const overflows = scroller.scrollWidth > scroller.offsetWidth;
  slideshow.classList.toggle('has-overflow', overflows);
  if (overflows) {
    syncButtons(slideshow);
  } else {
    slideshow.classList.remove('both-arrows-disabled');
  }
}

/** @param {HTMLElement} slideshow */
function initSlideshow(slideshow) {
  const scroller = /** @type {HTMLElement | null} */ (slideshow.querySelector('slideshow-slides'));

  updateOverflow(slideshow);

  if (scroller) {
    scroller.addEventListener(
      'scroll',
      () => {
        if (slideshow.classList.contains('has-overflow')) syncButtons(slideshow);
      },
      { passive: true },
    );
  }

  new ResizeObserver(() => updateOverflow(slideshow)).observe(slideshow);

  let pendingRecompute = false;
  new MutationObserver(() => {
    if (pendingRecompute) return;
    pendingRecompute = true;
    requestAnimationFrame(() => {
      pendingRecompute = false;
      updateOverflow(slideshow);
    });
  }).observe(slideshow, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['class'],
  });

  // The native slideshow (assets/slideshow.js) derives arrow disabled state from its
  // center-based `current` index, which flags the prev arrow as enabled at scrollLeft 0
  // (our cards are narrow, so the centered slide is index 1). It re-runs that logic on
  // ResizeObserver triggers — e.g. when the mini-PDP drawer's scroll-lock changes the
  // body width — clobbering our scroll-based state. Re-assert ours whenever it does.
  const nextBtn = /** @type {HTMLButtonElement | null} */ (slideshow.querySelector('[ref="next"]'));
  const prevBtn = /** @type {HTMLButtonElement | null} */ (
    slideshow.querySelector('[ref="previous"]')
  );
  if (nextBtn && prevBtn) {
    let reasserting = false;
    const disabledObserver = new MutationObserver(() => {
      if (reasserting) return;
      if (!slideshow.classList.contains('has-overflow')) return;
      reasserting = true;
      syncButtons(slideshow);
      reasserting = false;
    });
    disabledObserver.observe(prevBtn, { attributes: true, attributeFilter: ['disabled'] });
    disabledObserver.observe(nextBtn, { attributes: true, attributeFilter: ['disabled'] });
  }
}

function init() {
  document.querySelectorAll('.product-hotspot-recs-slideshow').forEach(initSlideshow);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
